import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Card, Row, Col, Select, Input, InputNumber, Button, Spin, Table, Segmented, App, Space,
  Typography, Empty, DatePicker, Radio, Switch, Tooltip,
} from 'antd';
import {
  TableOutlined, LineChartOutlined, DownloadOutlined, EnvironmentOutlined,
  KeyOutlined, CheckOutlined, DeleteOutlined, ThunderboltOutlined,
} from '@ant-design/icons';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  Legend, ResponsiveContainer, LabelList,
} from 'recharts';
import dayjs, { type Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import tz from 'dayjs/plugin/timezone';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../contexts/useTheme';
import { useChartAnimations } from '../../contexts/useChartAnimations';
import { useForecastFields } from '../../hooks/useForecastFields';
import eventsService, { type EventWithCircuit } from '../../api/eventsService';
import { hasForecastType } from '../../api/forecastFieldsService';
import { TIO_API_BASE_URL } from '../../utils/constants';
import { CORE_WEATHER_FIELDS, CHARTABLE_FIELDS, CHART_COLORS, type WeatherField } from './coreWeatherFields';
import { LocationPickerModal } from './LocationPickerModal';
import { convertWindToKmh } from '../../utils/weatherUtils';
import { getTomorrowIoWeatherCode } from '../../utils/tomorrowIoWeatherCodes';

dayjs.extend(utc);
dayjs.extend(tz);

const { Option } = Select;
const { Text } = Typography;

// localStorage key - same as API Docs page
const API_KEY_STORAGE_KEY = 'api-docs-test-api-key';

// ── Types ──────────────────────────────────────────────────────────────────

interface TimelineInterval {
  startTime: string;
  values: Record<string, number | string | null>;
}

interface FlattenedRow {
  key: string;
  startTime: string;
  locationLat: number;
  locationLon: number;
  values: Record<string, number | string | null>;
}

// ── Helpers ────────────────────────────────────────────────────────────────

const escapeCSV = (val: unknown): string => {
  const s = String(val ?? '');
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

// ── Constants ──────────────────────────────────────────────────────────────

const TIMESTEPS = [
  { label: '1h', value: '1h' },
  { label: '1d', value: '1d' },
  { label: '30m', value: '30m' },
  { label: '15m', value: '15m' },
  { label: '5m', value: '5m' },
  { label: '1m', value: '1m' },
  { label: 'current', value: 'current' },
  { label: 'best', value: 'best' },
];

const UNITS = [
  { label: 'Metric', value: 'metric' },
  { label: 'Imperial', value: 'imperial' },
];

const WIND_FIELD_NAMES = new Set([
  'windSpeed', 'windSpeedMin', 'windSpeedMax', 'windSpeedAvg',
  'windGust', 'windGustMin', 'windGustMax', 'windGustAvg',
]);

const WEATHER_CODE_FIELD_NAMES = new Set([
  'weatherCode', 'weatherCodeMin', 'weatherCodeMax',
  'weatherCodeFullDay', 'weatherCodeFullDayMin', 'weatherCodeFullDayMax',
  'weatherCodeDay', 'weatherCodeDayMin', 'weatherCodeDayMax',
  'weatherCodeNight', 'weatherCodeNightMin', 'weatherCodeNightMax',
]);

const getWeatherCodeType = (fieldName: string): 'simple' | 'fullDay' | 'day' | 'night' | undefined => {
  if (fieldName.startsWith('weatherCodeDay')) return 'day';
  if (fieldName.startsWith('weatherCodeNight')) return 'night';
  if (fieldName.startsWith('weatherCodeFullDay')) return 'fullDay';
  return 'simple';
};

/** Lookup IANA timezone from lat/lon using the free TimeAPI service */
const fetchTimezoneForCoords = async (latitude: number, longitude: number): Promise<string> => {
  const res = await fetch(
    `https://www.timeapi.io/api/timezone/coordinate?latitude=${latitude}&longitude=${longitude}`,
  );
  if (!res.ok) throw new Error('Timezone lookup failed');
  const data = await res.json();
  return data.timeZone as string; // e.g. "America/New_York"
};

// ── Component ──────────────────────────────────────────────────────────────

export const TimelinesApiPage = () => {
  const { authState } = useAuth();
  const user = authState.user;
  const { theme } = useTheme();
  const { chartAnimationsEnabled } = useChartAnimations();
  const { modal, message } = App.useApp();
  const { fields: forecastFields, loading: forecastFieldsLoading } = useForecastFields();

  // API key (localStorage)
  const [apiKey, setApiKeyState] = useState<string | null>(() => localStorage.getItem(API_KEY_STORAGE_KEY));
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [showApiKeyInput, setShowApiKeyInput] = useState(() => !localStorage.getItem(API_KEY_STORAGE_KEY));

  const setApiKey = useCallback((key: string | null) => {
    setApiKeyState(key);
    if (key) {
      localStorage.setItem(API_KEY_STORAGE_KEY, key);
    } else {
      localStorage.removeItem(API_KEY_STORAGE_KEY);
    }
  }, []);

  // Form state
  const [lat, setLat] = useState<number | null>(null);
  const [lon, setLon] = useState<number | null>(null);
  const [startTime, setStartTime] = useState<Dayjs | null>(() => dayjs().startOf('hour'));
  const [endTime, setEndTime] = useState<Dayjs | null>(() => dayjs().add(24, 'hour').startOf('hour'));
  const [timeMode, setTimeMode] = useState<'utc' | 'local'>('utc');
  const [detectedTimezone, setDetectedTimezone] = useState<string | null>(null);
  const [loadingTimezone, setLoadingTimezone] = useState(false);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [timestep, setTimestep] = useState('1h');
  const [dailyStartHour, setDailyStartHour] = useState(6);
  const [units, setUnits] = useState('metric');
  const [locationPickerOpen, setLocationPickerOpen] = useState(false);

  // Next race event
  const [nextEvent, setNextEvent] = useState<EventWithCircuit | null>(null);
  const [loadingNextEvent, setLoadingNextEvent] = useState(false);

  // Data
  const [loading, setLoading] = useState(false);
  const [timelinesData, setTimelinesData] = useState<FlattenedRow[]>([]);
  const [rawTimelinesResponse, setRawTimelinesResponse] = useState<Record<string, unknown> | null>(null);
  const [viewMode, setViewMode] = useState<string>('table');

  // Chart view options (aligned with AllStationsComparison)
  const [chartType, setChartType] = useState<'line' | 'bar'>('line');
  const [showDataLabels, setShowDataLabels] = useState(false);
  const [yAxisSide, setYAxisSide] = useState<'left' | 'right'>('left');
  const [yAxisScale, setYAxisScale] = useState<'auto' | 'custom'>('auto');
  const [yAxisStartValue, setYAxisStartValue] = useState(0);
  const [useDualYAxis, setUseDualYAxis] = useState(false);

  // Measure table/chart container for dynamic scroll and chart height
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState<number>(400);
  useEffect(() => {
    const el = tableContainerRef.current;
    if (!el) return;
    const update = () => {
      if (!el.isConnected) return;
      setContainerHeight(Math.max(300, el.clientHeight - 48)); // reserve for toolbar
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [timelinesData.length, viewMode]);

  // Detect timezone when lat/lon changes or timeMode switches to 'local'
  useEffect(() => {
    if (timeMode !== 'local' || lat === null || lon === null || isNaN(lat) || isNaN(lon)) {
      return;
    }
    let cancelled = false;
    setLoadingTimezone(true);
    fetchTimezoneForCoords(lat, lon)
      .then((tz) => {
        if (!cancelled) setDetectedTimezone(tz);
      })
      .catch(() => {
        if (!cancelled) setDetectedTimezone(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingTimezone(false);
      });
    return () => { cancelled = true; };
  }, [lat, lon, timeMode]);

  // Clear detected timezone when switching back to UTC
  useEffect(() => {
    if (timeMode === 'utc') {
      setDetectedTimezone(null);
    }
  }, [timeMode]);

  // The effective timezone used for the API call
  const effectiveTimezone = timeMode === 'local' && detectedTimezone ? detectedTimezone : 'UTC';

  // Fetch the next upcoming event (within 14 days)
  useEffect(() => {
    let cancelled = false;
    setLoadingNextEvent(true);
    eventsService.getAllEvents(dayjs().year())
      .then((response) => {
        if (cancelled) return;
        const events = response.data ?? [];
        const now = dayjs();
        const in14Days = now.add(14, 'day');
        const upcoming = events
          .filter((e) => e.status === 'Future' && !e.legacy && !e.demo)
          .sort((a, b) => a.start_date.localeCompare(b.start_date));
        const next = upcoming.find((e) => dayjs(e.start_date).isBefore(in14Days));
        setNextEvent(next ?? null);
      })
      .catch(() => { if (!cancelled) setNextEvent(null); })
      .finally(() => { if (!cancelled) setLoadingNextEvent(false); });
    return () => { cancelled = true; };
  }, []);

  const coreFieldNames = useMemo(() => new Set(CORE_WEATHER_FIELDS.map((f) => f.name)), []);

  const orderedSelectedFields = useMemo(() => {
    if (forecastFields.length === 0) return selectedFields;
    const posMap = new Map<string, number>();
    const snakeToCamel = (s: string) => s.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
    forecastFields.forEach((f) => {
      const camel = coreFieldNames.has(f.api_name) ? f.api_name : snakeToCamel(f.api_name);
      if (coreFieldNames.has(camel)) {
        posMap.set(camel, f.position ?? 999);
      }
    });
    return [...selectedFields].sort((a, b) => {
      const posA = posMap.get(a) ?? 999;
      const posB = posMap.get(b) ?? 999;
      return posA - posB || a.localeCompare(b);
    });
  }, [selectedFields, forecastFields, coreFieldNames]);

  const nextEventAvailable = !!nextEvent && !loadingNextEvent && !forecastFieldsLoading;

  const handleGenerateNextRaceForecast = useCallback((type: 'hourly' | 'daily') => {
    if (!nextEvent) return;
    const { circuit, start_date, end_date } = nextEvent;
    if (circuit.latitude == null || circuit.longitude == null) {
      message.warning('Next event circuit is missing coordinates.');
      return;
    }

    setLat(circuit.latitude);
    setLon(circuit.longitude);

    const eventStart = dayjs(start_date).startOf('day');
    const eventEnd = dayjs(end_date).endOf('day');
    setStartTime(eventStart);
    setEndTime(eventEnd);

    setTimeMode('local');

    const relevantFields = forecastFields
      .filter((f) => {
        if (f.hidden || f.calculated) return false;
        return hasForecastType(f, type);
      })
      .map((f) => f.api_name)
      .filter((name) => coreFieldNames.has(name));

    setSelectedFields(relevantFields);
    setTimestep(type === 'hourly' ? '1h' : '1d');

    message.success(`Loaded ${nextEvent.name} ${type} forecast settings. Review and click Fetch Data.`);
  }, [nextEvent, forecastFields, coreFieldNames, message]);

  // ── Access check ───────────────────────────────────────────────────────
  const hasAccess = user?.role === 'root' || user?.role === 'account-admin';

  // ── API Key handlers ───────────────────────────────────────────────────
  const handleSaveApiKey = () => {
    if (apiKeyInput.trim()) {
      setApiKey(apiKeyInput.trim());
      setShowApiKeyInput(false);
      setApiKeyInput('');
    }
  };

  const handleClearApiKey = () => {
    setApiKey(null);
    setShowApiKeyInput(true);
    setApiKeyInput('');
  };

  // ── Fetch Data ─────────────────────────────────────────────────────────
  const hasLocation = lat !== null && lon !== null && !isNaN(lat) && !isNaN(lon);
  const canFetch = apiKey && hasLocation && startTime && endTime && selectedFields.length > 0;

  const handleFetch = () => {
    if (!startTime || !endTime) {
      modal.error({ title: 'Missing Required Fields', content: 'Please select both start time and end time.' });
      return;
    }
    if (!hasLocation) {
      modal.error({ title: 'Missing Location', content: 'Please enter latitude and longitude.' });
      return;
    }
    if (timeMode === 'local' && !detectedTimezone) {
      modal.error({ title: 'Timezone Not Detected', content: 'Could not detect the local timezone for the given coordinates. Please try again or switch to UTC.' });
      return;
    }

    modal.confirm({
      title: 'Fetch Timelines Data?',
      content: 'This will make 1 API call using your API key.',
      okText: 'Fetch',
      onOk: () => fetchTimelinesData(),
    });
  };

  const fetchTimelinesData = async () => {
    setLoading(true);
    try {
      // When Local: treat user's selection as location's local time, convert to UTC for API
      // When UTC: treat as UTC. dayjs.tz(str, tz) parses str in that timezone, .utc() converts to UTC
      const startStr = startTime!.format('YYYY-MM-DD HH:mm');
      const endStr = endTime!.format('YYYY-MM-DD HH:mm');
      const formattedStart = dayjs.tz(startStr, effectiveTimezone).utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
      const formattedEnd = dayjs.tz(endStr, effectiveTimezone).utc().format('YYYY-MM-DDTHH:mm:ss[Z]');

      const body: Record<string, unknown> = {
        location: [lat, lon],
        fields: selectedFields,
        timesteps: [timestep],
        startTime: formattedStart,
        endTime: formattedEnd,
        timezone: effectiveTimezone,
        units,
      };
      if (timestep === '1d') {
        body.dailyStartHour = dailyStartHour;
      }

      const res = await fetch(`${TIO_API_BASE_URL}/timelines`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: apiKey!,
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || `API error ${res.status}`);
      }

      const data = await res.json();
      setRawTimelinesResponse(data);
      const intervals: TimelineInterval[] = data.data?.timelines?.[0]?.intervals ?? [];

      const rows: FlattenedRow[] = intervals.map((interval, idx) => ({
        key: `${lat}-${lon}-${idx}`,
        startTime: interval.startTime,
        locationLat: lat!,
        locationLon: lon!,
        values: interval.values,
      }));

      setTimelinesData(rows);
      message.success(`Fetched ${rows.length} data points.`);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      message.error(`Error fetching timelines data: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  // ── Table columns ──────────────────────────────────────────────────────
  const columns = useMemo(() => {
    const fieldMap = new Map(CORE_WEATHER_FIELDS.map((f) => [f.name, f]));
    const formatTime = (text: string) =>
      effectiveTimezone === 'UTC'
        ? dayjs(text).utc().format('YYYY-MM-DD HH:mm:ss[Z]')
        : dayjs(text).utc().tz(effectiveTimezone).format('YYYY-MM-DD HH:mm');
    const cols = [
      {
        title: `Start Time${effectiveTimezone !== 'UTC' ? ` (${effectiveTimezone})` : ''}`,
        dataIndex: 'startTime',
        key: 'startTime',
        width: 200,
        render: (text: string) => formatTime(text),
      },
    ];

    orderedSelectedFields.forEach((fieldName) => {
      const fieldDef = fieldMap.get(fieldName);
      const isWind = WIND_FIELD_NAMES.has(fieldName);
      const isWeatherCode = WEATHER_CODE_FIELD_NAMES.has(fieldName);
      const unitLabel = isWind ? 'km/h' : (fieldDef ? fieldDef.unit[units as 'metric' | 'imperial'] : '');
      cols.push({
        title: `${fieldDef?.label ?? fieldName}${unitLabel ? ` (${unitLabel})` : ''}`,
        dataIndex: ['values', fieldName] as unknown as string,
        key: fieldName,
        width: isWeatherCode ? 180 : 150,
        render: (value: unknown) => {
          if (value == null) return '—';
          if (isWind) {
            const kmh = convertWindToKmh(value as number | string | null);
            return kmh != null ? `${Math.round(kmh)} km/h` : '—';
          }
          if (isWeatherCode) {
            const codeStr = String(value);
            const info = getTomorrowIoWeatherCode(codeStr, getWeatherCodeType(fieldName));
            return (
              <Tooltip title={info.code ? `${info.code}: ${info.label}` : info.label}>
                <span style={{ cursor: 'help' }}>{info.label}</span>
              </Tooltip>
            ) as unknown as string;
          }
          return String(value);
        },
      });
    });

    return cols;
  }, [orderedSelectedFields, units, effectiveTimezone]);

  // ── Chart data ─────────────────────────────────────────────────────────
  const chartableSelectedFields = useMemo(() => {
    const chartableNames = new Set(CHARTABLE_FIELDS.map((f) => f.name));
    return orderedSelectedFields.filter((f) => chartableNames.has(f));
  }, [orderedSelectedFields]);

  // Reset dual y-axis when number of chartable parameters is not 2
  useEffect(() => {
    if (chartableSelectedFields.length !== 2 && useDualYAxis) {
      setUseDualYAxis(false);
    }
  }, [chartableSelectedFields.length, useDualYAxis]);

  const chartData = useMemo(() => {
    if (timelinesData.length === 0 || chartableSelectedFields.length === 0) return [];
    const formatChartTime = (iso: string) =>
      effectiveTimezone === 'UTC'
        ? dayjs(iso).utc().format('MM/DD HH:mm')
        : dayjs(iso).utc().tz(effectiveTimezone).format('MM/DD HH:mm');
    return timelinesData.map((row) => {
      const point: Record<string, unknown> = {
        time: formatChartTime(row.startTime),
        fullTime: row.startTime,
        location: `${row.locationLat}, ${row.locationLon}`,
      };
      chartableSelectedFields.forEach((fieldName) => {
        const val = row.values[fieldName];
        if (WIND_FIELD_NAMES.has(fieldName)) {
          const kmh = convertWindToKmh(val);
          point[fieldName] = kmh;
        } else {
          point[fieldName] = typeof val === 'number' ? val : null;
        }
      });
      return point;
    });
  }, [timelinesData, chartableSelectedFields, effectiveTimezone]);

  // Parameter value ranges for Y-axis domain calculation
  const parameterRanges = useMemo(() => {
    if (chartData.length === 0 || chartableSelectedFields.length === 0) return [];
    const ranges: { param: string; min: number; max: number; center: number }[] = [];
    chartableSelectedFields.forEach((param) => {
      const values = chartData
        .map((row) => row[param])
        .filter((v): v is number => typeof v === 'number' && !Number.isNaN(v));
      if (values.length === 0) return;
      const minValue = Math.min(...values);
      const maxValue = Math.max(...values);
      ranges.push({
        param,
        min: minValue,
        max: maxValue,
        center: (minValue + maxValue) / 2,
      });
    });
    return ranges.sort((a, b) => a.center - b.center);
  }, [chartData, chartableSelectedFields]);

  // Y-axis groups: single axis or dual axis (when 2 params and useDualYAxis)
  const yAxisGroups = useMemo(() => {
    if (parameterRanges.length === 0) return [];

    const overallMin = Math.min(...parameterRanges.map((r) => r.min));
    const overallMax = Math.max(...parameterRanges.map((r) => r.max));
    const overallRange = overallMax - overallMin;
    const padding = overallRange > 0 ? overallRange * 0.05 : Math.max(1, Math.abs(overallMin) * 0.1);

    const applyYAxisScale = (min: number, max: number): [number, number] => {
      const top = Math.ceil(max + padding);
      if (yAxisScale === 'custom') {
        const bottom = Math.min(yAxisStartValue, Math.floor(min - padding));
        return [bottom, top];
      }
      return [Math.floor(min - padding), top];
    };

    if (!useDualYAxis || parameterRanges.length === 1) {
      return [{
        yAxisId: yAxisSide,
        parameters: parameterRanges.map((r) => r.param),
        domain: applyYAxisScale(overallMin, overallMax),
      }];
    }

    const midPoint = Math.floor(parameterRanges.length / 2);
    const lowerGroup = parameterRanges.slice(0, midPoint);
    const upperGroup = parameterRanges.slice(midPoint);

    const lowerMin = Math.min(...lowerGroup.map((r) => r.min));
    const lowerMax = Math.max(...lowerGroup.map((r) => r.max));
    const lowerRange = lowerMax - lowerMin;
    const lowerPadding = lowerRange > 0 ? lowerRange * 0.05 : Math.max(1, Math.abs(lowerMin) * 0.1);

    const upperMin = Math.min(...upperGroup.map((r) => r.min));
    const upperMax = Math.max(...upperGroup.map((r) => r.max));
    const upperRange = upperMax - upperMin;
    const upperPadding = upperRange > 0 ? upperRange * 0.05 : Math.max(1, Math.abs(upperMin) * 0.1);

    const applyLower = (min: number, max: number): [number, number] => {
      const top = Math.ceil(max + lowerPadding);
      if (yAxisScale === 'custom') {
        const bottom = Math.min(yAxisStartValue, Math.floor(min - lowerPadding));
        return [bottom, top];
      }
      return [Math.floor(min - lowerPadding), top];
    };
    const applyUpper = (min: number, max: number): [number, number] => {
      const top = Math.ceil(max + upperPadding);
      if (yAxisScale === 'custom') {
        const bottom = Math.min(yAxisStartValue, Math.floor(min - upperPadding));
        return [bottom, top];
      }
      return [Math.floor(min - upperPadding), top];
    };

    return [
      { yAxisId: 'left', parameters: lowerGroup.map((r) => r.param), domain: applyLower(lowerMin, lowerMax) },
      { yAxisId: 'right', parameters: upperGroup.map((r) => r.param), domain: applyUpper(upperMin, upperMax) },
    ];
  }, [parameterRanges, useDualYAxis, yAxisSide, yAxisScale, yAxisStartValue]);

  // Chart series (Line or Bar) with yAxisId and optional data labels
  const chartSeries = useMemo(() => {
    const getYAxisIdForParameter = (param: string): string => {
      const group = yAxisGroups.find((g) => g.parameters.includes(param));
      return group?.yAxisId ?? 'left';
    };

    return chartableSelectedFields.map((fieldName, idx) => {
      const fieldDef = CORE_WEATHER_FIELDS.find((f) => f.name === fieldName);
      const unitLabel = WIND_FIELD_NAMES.has(fieldName) ? 'km/h' : (fieldDef ? fieldDef.unit[units as 'metric' | 'imperial'] : '');
      const name = `${fieldDef?.label ?? fieldName}${unitLabel ? ` (${unitLabel})` : ''}`;
      const color = CHART_COLORS[idx % CHART_COLORS.length];
      const yAxisId = getYAxisIdForParameter(fieldName);

      const labelList = showDataLabels ? (
        <LabelList
          dataKey={fieldName}
          position="top"
          fill={chartType === 'line' ? color : theme === 'dark' ? '#fff' : '#000'}
          fontSize={10}
          formatter={(value: unknown) => typeof value === 'number' ? value.toFixed(1) : String(value ?? '')}
        />
      ) : null;

      if (chartType === 'line') {
        return (
          <Line
            key={fieldName}
            type="monotone"
            dataKey={fieldName}
            name={name}
            stroke={color}
            dot={false}
            strokeWidth={2}
            isAnimationActive={chartAnimationsEnabled}
            connectNulls
            yAxisId={yAxisId}
          >
            {labelList}
          </Line>
        );
      }
      return (
        <Bar
          key={fieldName}
          dataKey={fieldName}
          name={name}
          fill={color}
          isAnimationActive={chartAnimationsEnabled}
          yAxisId={yAxisId}
        >
          {labelList}
        </Bar>
      );
    });
  }, [chartableSelectedFields, chartType, showDataLabels, yAxisGroups, units, theme, chartAnimationsEnabled]);

  // ── Export functions ────────────────────────────────────────────────────
  const exportCSV = () => {
    if (timelinesData.length === 0) return;
    const formatForExport = (iso: string) =>
      effectiveTimezone === 'UTC'
        ? dayjs(iso).utc().format('YYYY-MM-DDTHH:mm:ss[Z]')
        : dayjs(iso).utc().tz(effectiveTimezone).format('YYYY-MM-DD HH:mm');

    const headers = columns.map((c) => c.title as string);
    const csvRows = [headers.map(escapeCSV).join(',')];

    const getCellValue = (col: (typeof columns)[0], row: FlattenedRow): string => {
      if (col.key === 'startTime') return formatForExport(row.startTime);
      if (col.key && col.key !== 'startTime') {
        const val = row.values[col.key];
        if (val == null) return '—';
        if (WIND_FIELD_NAMES.has(col.key)) {
          const kmh = convertWindToKmh(val);
          return kmh != null ? `${Math.round(kmh)} km/h` : '—';
        }
        if (WEATHER_CODE_FIELD_NAMES.has(col.key)) {
          const info = getTomorrowIoWeatherCode(String(val), getWeatherCodeType(col.key));
          return info.label;
        }
        return String(val);
      }
      return '';
    };

    timelinesData.forEach((row) => {
      const vals = columns.map((col) => getCellValue(col, row));
      csvRows.push(vals.map(escapeCSV).join(','));
    });

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `timelines-data-${dayjs().format('YYYY-MM-DD-HHmmss')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportJSON = () => {
    if (!rawTimelinesResponse) return;
    const blob = new Blob([JSON.stringify(rawTimelinesResponse, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `timelines-data-${dayjs().format('YYYY-MM-DD-HHmmss')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Field options for select ───────────────────────────────────────────
  const fieldOptions = CORE_WEATHER_FIELDS.map((f: WeatherField) => ({
    label: f.label,
    value: f.name,
  }));

  // ── Render ─────────────────────────────────────────────────────────────
  const isDark = theme === 'dark';

  if (!hasAccess) {
    return (
      <div style={{ padding: 24 }}>
        <Empty description="You do not have permission to access this page." />
      </div>
    );
  }

  return (
    <Spin spinning={loading} tip="Fetching timelines data...">
      <div
        style={{
          height: 'calc(100vh - 64px)',
          maxHeight: 'calc(100vh - 64px)',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          padding: 16,
          background: isDark ? '#141414' : '#ffffff',
        }}
      >
        <Card
          style={{ marginBottom: 12 }}
          styles={{ body: { padding: '16px' } }}
        >
          {/* API Key Row */}
          <div style={{ marginBottom: 12 }}>
            {!showApiKeyInput && apiKey ? (
              <Space>
                <KeyOutlined />
                <Text type="success">
                  API Key configured
                  <span style={{ opacity: 0.7 }}>
                    {' '}({apiKey.substring(0, 8)}...{apiKey.substring(apiKey.length - 4)})
                  </span>
                </Text>
                <Button size="small" onClick={() => setShowApiKeyInput(true)}>Change</Button>
                <Button size="small" danger icon={<DeleteOutlined />} onClick={handleClearApiKey}>Clear</Button>
              </Space>
            ) : (
              <Space.Compact style={{ width: '100%', maxWidth: 600 }}>
                <Input.Password
                  placeholder="Enter your Tomorrow.io API key"
                  value={apiKeyInput}
                  onChange={(e) => setApiKeyInput(e.target.value)}
                  onPressEnter={handleSaveApiKey}
                  prefix={<KeyOutlined />}
                  style={{ flex: 1 }}
                />
                <Button type="primary" icon={<CheckOutlined />} onClick={handleSaveApiKey} disabled={!apiKeyInput.trim()}>
                  Save
                </Button>
                {apiKey && <Button onClick={() => setShowApiKeyInput(false)}>Cancel</Button>}
              </Space.Compact>
            )}
          </div>

          {/* Next Race Forecast Buttons */}
          {nextEventAvailable && (
            <div style={{ marginBottom: 12 }}>
              <Space>
                <ThunderboltOutlined style={{ color: '#faad14' }} />
                <Text style={{ fontSize: 13 }}>
                  <strong>{nextEvent!.name}</strong>
                  <span style={{ opacity: 0.7 }}> ({dayjs(nextEvent!.start_date).format('MMM D')} – {dayjs(nextEvent!.end_date).format('MMM D')})</span>
                </Text>
                <Button
                  size="small"
                  onClick={() => handleGenerateNextRaceForecast('hourly')}
                >
                  Hourly Forecast
                </Button>
                <Button
                  size="small"
                  onClick={() => handleGenerateNextRaceForecast('daily')}
                >
                  Daily Forecast
                </Button>
              </Space>
            </div>
          )}

          {/* Filters Row */}
          <Row gutter={8} align="bottom">
            <Col>
              <div style={{ marginBottom: 4, fontSize: 12, color: isDark ? '#999' : '#666' }}>Latitude</div>
              <InputNumber
                placeholder="e.g. 40.7128"
                value={lat}
                onChange={(val) => setLat(val)}
                step={0.000001}
                precision={6}
                style={{ width: 140 }}
                min={-90}
                max={90}
              />
            </Col>
            <Col>
              <div style={{ marginBottom: 4, fontSize: 12, color: isDark ? '#999' : '#666' }}>Longitude</div>
              <InputNumber
                placeholder="e.g. -74.0060"
                value={lon}
                onChange={(val) => setLon(val)}
                step={0.000001}
                precision={6}
                style={{ width: 140 }}
                min={-180}
                max={180}
              />
            </Col>
            <Col>
              <div style={{ marginBottom: 4, fontSize: 12, color: isDark ? '#999' : '#666' }}>&nbsp;</div>
              <Button
                icon={<EnvironmentOutlined />}
                onClick={() => setLocationPickerOpen(true)}
                title="Pick location on map"
              />
            </Col>
            <Col>
              <div style={{ marginBottom: 4, fontSize: 12, color: isDark ? '#999' : '#666' }}>
                Time{timeMode === 'local' && detectedTimezone ? ` (${detectedTimezone})` : timeMode === 'local' && loadingTimezone ? ' (detecting...)' : ' (UTC)'}
              </div>
              <Segmented
                value={timeMode}
                onChange={(val) => setTimeMode(val as 'utc' | 'local')}
                options={[
                  { label: 'UTC', value: 'utc' },
                  { label: 'Local', value: 'local' },
                ]}
              />
            </Col>
            <Col>
              <div style={{ marginBottom: 4, fontSize: 12, color: isDark ? '#999' : '#666' }}>Start Time</div>
              <DatePicker
                showTime={{ format: 'HH:mm' }}
                format="YYYY-MM-DD HH:mm"
                value={startTime}
                onChange={setStartTime}
                style={{ width: 185 }}
                placeholder="Select start time"
              />
            </Col>
            <Col>
              <div style={{ marginBottom: 4, fontSize: 12, color: isDark ? '#999' : '#666' }}>End Time</div>
              <DatePicker
                showTime={{ format: 'HH:mm' }}
                format="YYYY-MM-DD HH:mm"
                value={endTime}
                onChange={setEndTime}
                style={{ width: 185 }}
                placeholder="Select end time"
              />
            </Col>
            <Col flex="1">
              <div style={{ marginBottom: 4, fontSize: 12, color: isDark ? '#999' : '#666' }}>Fields</div>
              <Select
                mode="multiple"
                style={{ width: '100%' }}
                placeholder="Select weather fields"
                value={selectedFields}
                onChange={setSelectedFields}
                maxTagCount={2}
                showSearch
                filterOption={(input, option) =>
                  (option?.label as string)?.toLowerCase().includes(input.toLowerCase()) ||
                  (option?.value as string)?.toLowerCase().includes(input.toLowerCase())
                }
                options={fieldOptions}
              />
            </Col>
            <Col>
              <div style={{ marginBottom: 4, fontSize: 12, color: isDark ? '#999' : '#666' }}>Timestep</div>
              <Select style={{ width: 110 }} value={timestep} onChange={setTimestep}>
                {TIMESTEPS.map((t) => (
                  <Option key={t.value} value={t.value}>{t.label}</Option>
                ))}
              </Select>
            </Col>
            {timestep === '1d' && (
              <Col>
                <div style={{ marginBottom: 4, fontSize: 12, color: isDark ? '#999' : '#666' }} title="Hour when daily aggregation period begins. 0=midnight, 6=6 AM (default).">
                  Daily Start Hour
                </div>
                <InputNumber
                  min={0}
                  max={23}
                  value={dailyStartHour}
                  onChange={(val) => val != null && val >= 0 && val <= 23 && setDailyStartHour(val)}
                  style={{ width: 70 }}
                />
              </Col>
            )}
            <Col>
              <div style={{ marginBottom: 4, fontSize: 12, color: isDark ? '#999' : '#666' }}>Units</div>
              <Select style={{ width: 110 }} value={units} onChange={setUnits}>
                {UNITS.map((u) => (
                  <Option key={u.value} value={u.value}>{u.label}</Option>
                ))}
              </Select>
            </Col>
            <Col>
              <Button type="primary" onClick={handleFetch} disabled={!canFetch}>
                Fetch Data
              </Button>
            </Col>
          </Row>
        </Card>

        {/* Results Card */}
        <Card
          bordered
          style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
          styles={{ body: { padding: '12px 16px', flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' } }}
        >
          {/* Toolbar */}
          <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
            <Col>
              <Segmented
                value={viewMode}
                onChange={(val) => setViewMode(val as string)}
                options={[
                  { label: 'Table', value: 'table', icon: <TableOutlined /> },
                  { label: 'Chart', value: 'chart', icon: <LineChartOutlined /> },
                ]}
              />
            </Col>
            {timelinesData.length > 0 && (
              <Col>
                <Space>
                  <Button icon={<DownloadOutlined />} onClick={exportCSV} size="small">
                    Export CSV
                  </Button>
                  <Button icon={<DownloadOutlined />} onClick={exportJSON} size="small">
                    Export JSON
                  </Button>
                </Space>
              </Col>
            )}
          </Row>

          {/* View Content */}
          {timelinesData.length === 0 ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
              <Empty description="No data. Configure parameters and click Fetch Data." />
            </div>
          ) : (
            <div ref={tableContainerRef} style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              {viewMode === 'table' ? (
                <Table
                  dataSource={timelinesData}
                  columns={columns}
                  rowKey="key"
                  scroll={{ y: Math.max(200, containerHeight - 55), x: 'max-content' }}
                  pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: ['25', '50', '100', '200'] }}
                  size="small"
                  bordered
                />
              ) : chartableSelectedFields.length === 0 ? (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Empty description="No chartable (numeric) fields selected." />
                </div>
              ) : (
                <>
                  {/* Chart controls */}
                  <Row gutter={[12, 8]} align="middle" style={{ marginBottom: 12 }}>
                    <Col>
                      <Space size="small" align="center">
                        <span style={{ fontSize: 12, color: isDark ? '#999' : '#666' }}>Chart type:</span>
                        <Radio.Group
                          value={chartType}
                          onChange={(e) => setChartType(e.target.value)}
                          buttonStyle="solid"
                          size="small"
                        >
                          <Radio.Button value="line">Line</Radio.Button>
                          <Radio.Button value="bar">Bar</Radio.Button>
                        </Radio.Group>
                      </Space>
                    </Col>
                    <Col>
                      <Space size="small" align="center">
                        <span style={{ fontSize: 12, color: isDark ? '#999' : '#666' }}>Data labels:</span>
                        <Switch
                          checked={showDataLabels}
                          onChange={setShowDataLabels}
                          checkedChildren="On"
                          unCheckedChildren="Off"
                          size="small"
                        />
                      </Space>
                    </Col>
                    <Col>
                      <Space size="small" align="center">
                        <span style={{ fontSize: 12, color: isDark ? '#999' : '#666' }}>Dual Y-axis:</span>
                        <Radio.Group
                          value={useDualYAxis ? 'dual' : 'single'}
                          onChange={(e) => setUseDualYAxis(e.target.value === 'dual')}
                          buttonStyle="solid"
                          size="small"
                          disabled={chartableSelectedFields.length !== 2}
                        >
                          <Radio.Button value="single">Single</Radio.Button>
                          <Radio.Button value="dual">Dual</Radio.Button>
                        </Radio.Group>
                      </Space>
                    </Col>
                    <Col>
                      <Space size="small" align="center">
                        <span style={{ fontSize: 12, color: isDark ? '#999' : '#666' }}>Y-axis:</span>
                        <Radio.Group
                          value={yAxisSide}
                          onChange={(e) => setYAxisSide(e.target.value)}
                          buttonStyle="solid"
                          size="small"
                          disabled={useDualYAxis}
                        >
                          <Radio.Button value="left">Left</Radio.Button>
                          <Radio.Button value="right">Right</Radio.Button>
                        </Radio.Group>
                      </Space>
                    </Col>
                    <Col>
                      <Space size="small" align="center">
                        <span style={{ fontSize: 12, color: isDark ? '#999' : '#666' }}>Y-axis scale:</span>
                        <Radio.Group
                          value={yAxisScale}
                          onChange={(e) => setYAxisScale(e.target.value)}
                          buttonStyle="solid"
                          size="small"
                        >
                          <Radio.Button value="auto">Auto</Radio.Button>
                          <Radio.Button value="custom">Start at</Radio.Button>
                        </Radio.Group>
                        {yAxisScale === 'custom' && (
                          <InputNumber
                            value={yAxisStartValue}
                            onChange={(v) => setYAxisStartValue(v ?? 0)}
                            size="small"
                            style={{ width: 72 }}
                            title="Y-axis minimum value"
                          />
                        )}
                      </Space>
                    </Col>
                  </Row>
                  <ResponsiveContainer width="100%" height={containerHeight - 48}>
                    {chartType === 'line' ? (
                      <LineChart
                        data={chartData}
                        margin={{
                          top: 5,
                          right: yAxisGroups.some((g) => g.yAxisId === 'right') ? 80 : 30,
                          left: yAxisGroups.some((g) => g.yAxisId === 'left') ? 20 : 10,
                          bottom: 5,
                        }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#333' : '#eee'} />
                        <XAxis
                          dataKey="time"
                          tick={{ fill: isDark ? '#aaa' : '#666', fontSize: 11 }}
                          stroke={isDark ? '#555' : '#ccc'}
                        />
                        {yAxisGroups.map((group) => (
                          <YAxis
                            key={group.yAxisId}
                            yAxisId={group.yAxisId}
                            orientation={group.yAxisId === 'left' ? 'left' : 'right'}
                            domain={group.domain}
                            tick={{ fill: isDark ? '#aaa' : '#666', fontSize: 11 }}
                            stroke={isDark ? '#555' : '#ccc'}
                            label={
                              yAxisGroups.length > 1
                                ? {
                                    value: group.parameters
                                      .map((p) => CORE_WEATHER_FIELDS.find((f) => f.name === p)?.label ?? p)
                                      .join(', '),
                                    angle: -90,
                                    position: group.yAxisId === 'left' ? 'insideLeft' : 'insideRight',
                                    style: { fill: isDark ? '#aaa' : '#666', fontSize: 11 },
                                  }
                                : undefined
                            }
                          />
                        ))}
                        <RechartsTooltip
                          contentStyle={{
                            backgroundColor: isDark ? '#1f1f1f' : '#fff',
                            border: `1px solid ${isDark ? '#333' : '#ddd'}`,
                            borderRadius: 6,
                            color: isDark ? '#ddd' : '#333',
                          }}
                        />
                        <Legend />
                        {chartSeries}
                      </LineChart>
                    ) : (
                      <BarChart
                        data={chartData}
                        margin={{
                          top: 5,
                          right: yAxisGroups.some((g) => g.yAxisId === 'right') ? 80 : 30,
                          left: yAxisGroups.some((g) => g.yAxisId === 'left') ? 20 : 10,
                          bottom: 5,
                        }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#333' : '#eee'} />
                        <XAxis
                          dataKey="time"
                          tick={{ fill: isDark ? '#aaa' : '#666', fontSize: 11 }}
                          stroke={isDark ? '#555' : '#ccc'}
                        />
                        {yAxisGroups.map((group) => (
                          <YAxis
                            key={group.yAxisId}
                            yAxisId={group.yAxisId}
                            orientation={group.yAxisId === 'left' ? 'left' : 'right'}
                            domain={group.domain}
                            tick={{ fill: isDark ? '#aaa' : '#666', fontSize: 11 }}
                            stroke={isDark ? '#555' : '#ccc'}
                            label={
                              yAxisGroups.length > 1
                                ? {
                                    value: group.parameters
                                      .map((p) => CORE_WEATHER_FIELDS.find((f) => f.name === p)?.label ?? p)
                                      .join(', '),
                                    angle: -90,
                                    position: group.yAxisId === 'left' ? 'insideLeft' : 'insideRight',
                                    style: { fill: isDark ? '#aaa' : '#666', fontSize: 11 },
                                  }
                                : undefined
                            }
                          />
                        ))}
                        <RechartsTooltip
                          contentStyle={{
                            backgroundColor: isDark ? '#1f1f1f' : '#fff',
                            border: `1px solid ${isDark ? '#333' : '#ddd'}`,
                            borderRadius: 6,
                            color: isDark ? '#ddd' : '#333',
                          }}
                        />
                        <Legend />
                        {chartSeries}
                      </BarChart>
                    )}
                  </ResponsiveContainer>
                </>
              )}
            </div>
          )}
        </Card>

        {/* Location Picker Modal */}
        <LocationPickerModal
          open={locationPickerOpen}
          onClose={() => setLocationPickerOpen(false)}
          onSelect={(pickedLat, pickedLon) => {
            setLat(pickedLat);
            setLon(pickedLon);
          }}
          initialLat={lat}
          initialLon={lon}
        />
      </div>
    </Spin>
  );
};
